import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getBaseUrl } from '../utils/getBaseUrl';
import { getCurrentUser } from '../utils/auth';
import { applyDynamicPwaBranding } from '../utils/pwaDynamic';
import { applyBrandTokens, DEFAULT_BRAND_HEX } from '../utils/applyBrandTokens';
import { isInstituteContext, BLANK_FAVICON, BASE_DOMAIN, isUnderBaseDomain } from '../utils/brandHost';
import {
  resolveBrandName,
  resolveBrandLogo,
  resolveBrandFavicon,
  resolveBrandSubtitle,
  BRAND_PRIMARY_COLOR,
} from '../constants/brand';

const BrandingContext = createContext();

const isLmsCampus = () => {
  const h = window.location.hostname;
  return isUnderBaseDomain(h) || h === 'localhost';
};

/**
 * Extract subdomain from current hostname.
 * e.g. "abc-academy.lmscampus.com" → "abc-academy"
 * Returns null for bare domain, localhost, or www.
 */
const getSubdomain = () => {
  const h = window.location.hostname;
  if (h === 'localhost') return null;
  const baseDomain = BASE_DOMAIN;
  if (!h.endsWith(`.${baseDomain}`)) return null;
  const sub = h.slice(0, h.length - baseDomain.length - 1);
  if (!sub || ['www', 'app', 'api', 'admin'].includes(sub)) return null;
  return sub;
};

// Build-time brand defaults. In an enterprise build the REACT_APP_BRAND_* values
// (resolved via constants/brand) take over, so the operator's name/logo are the
// default. In SaaS the default is the platform's own name on EVERY host — a
// custom domain is some institute's, and until its branding arrives the page
// must not wear a different institute's name.
const DEFAULTS = {
  brandName: resolveBrandName('LMS Campus'),
  legalName: '',
  brandSubtitle: resolveBrandSubtitle('Learning Management'),
  // DEFAULT_BRAND_HEX, not a literal: this feeds the PWA theme-color meta tag,
  // so an unbranded tenant's browser chrome has to be the same colour the palette
  // paints. It was a hardcoded `#2563eb` one step off `--primary`, which is the
  // kind of drift a second copy of a value always produces eventually.
  primaryColor: BRAND_PRIMARY_COLOR || DEFAULT_BRAND_HEX,
  // ── The LMS app theme ──────────────────────────────────────────────────────
  // ⚠️ NOT `primaryColor` above. That one is the MARKETING SITE's colour: vc_cms
  // reads it from /site-config and the PWA theme-color meta tag uses it. This
  // block is the app's own, and vc_cms never sees it.
  //
  // `mode: 'default'` means the palette wins and NOTHING is written inline. That
  // is the default state, and it is the whole point: before this existed, the
  // app read `primaryColor` and gated it on whether the institute had set a
  // brandName / logo / favicon — so uploading a logo silently switched on a
  // colour override nobody had chosen, and there was no way to ask for the
  // product defaults back. Design-system Phase 7.1, revised 2026-08-29.
  appTheme: BRAND_PRIMARY_COLOR
    ? { mode: 'custom', primary: BRAND_PRIMARY_COLOR, success: '', warning: '', danger: '', info: '' }
    : { mode: 'default', primary: '', success: '', warning: '', danger: '', info: '' },
  logoBgColor: '#3b82f6',
  logoUrl: resolveBrandLogo(null),
  faviconUrl: resolveBrandFavicon(null),
  pwaIconUrl: null,
  pwaSplashUrl: null,
  supportEmail: '',
  contactEmail: '',
  loginPage: null,
};

/**
 * What an INSTITUTE's page wears before its branding is known: nothing.
 *
 * The defaults above are the platform's own name and mark. On an institute's
 * subdomain or custom domain they are the one thing that must never show — a
 * white-labelled student watched "LMS Campus" turn into their institute's name
 * on every cold load. An empty name and no logo is the honest state while the
 * request is in flight, and consumers render a quiet placeholder for it.
 */
const EMPTY_BRAND = {
  ...DEFAULTS,
  brandName: '',
  brandSubtitle: '',
  logoUrl: null,
  faviconUrl: null,
};

/**
 * A server (or cached) `appTheme` → a usable block, or null if it says nothing.
 *
 * Returning NULL for "default" is what lets the merge below fall through from
 * the institute row to the platform row to `DEFAULTS` with a plain `||` chain.
 * An institute that has explicitly chosen `default` therefore inherits the
 * platform's theme if the platform set one — which is the same rule every other
 * branding field already follows.
 *
 * Tolerant of the field being absent: a settings document written before this
 * block existed has no `appTheme`, and that has to mean "defaults", not a crash.
 */
const normalizeAppTheme = (raw) => {
  if (!raw || typeof raw !== 'object' || raw.mode !== 'custom') return null;
  const hex = (v) => (typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v.trim()) ? v.trim().toLowerCase() : '');
  const out = {
    mode: 'custom',
    primary: hex(raw.primary),
    success: hex(raw.success),
    warning: hex(raw.warning),
    danger: hex(raw.danger),
    info: hex(raw.info),
  };
  // "Custom" with every colour blank is indistinguishable from "default", and
  // saying so here keeps applyBrandTokens from writing an empty inline style.
  const anySet = out.primary || out.success || out.warning || out.danger || out.info;
  return anySet ? out : null;
};

// ── Branding cache helpers ──────────────────────────────────
const CACHE_KEY = `vc-branding-${window.location.hostname}`;
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

const getCachedBranding = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    // Return cached data even if expired — we'll refresh in background
    return { data, expired: Date.now() - timestamp > CACHE_TTL };
  } catch {
    return null;
  }
};

const setCachedBranding = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {}
};

// Paint the cached brand colour during bundle parse, not in an effect. The
// provider's own useEffect below runs AFTER the first paint, so a branded
// institute would flash the palette violet on every cold load — the same class
// of bug as the pre-mount theme flash (Phase 1.2). Cheap, idempotent, and a
// no-op for a tenant with no branding or no cache.
try {
  const _boot = getCachedBranding();
  // A cache written before `appTheme` existed carries `primaryColorOverride` and
  // no theme, so this resolves to null and the palette stands until the fetch
  // returns — which is the correct reading of "this institute has not opted in".
  const _bootTheme = normalizeAppTheme(_boot?.data?.appTheme);
  if (_bootTheme) applyBrandTokens(_bootTheme);
} catch {}

/**
 * Build axios headers for branding requests.
 * Mirrors apiClient's X-Custom-Domain logic so the backend tenantResolver
 * can reliably resolve the institute even before the user has logged in.
 */
const getBrandingHeaders = () => {
  const hostname = window.location.hostname;
  if (hostname.endsWith('.localhost')) {
    const sub = hostname.slice(0, -('.localhost'.length));
    if (sub) return { 'X-Institute-Subdomain': sub };
    return {};
  }
  if (
    hostname !== 'localhost' &&
    !isUnderBaseDomain(hostname)
  ) {
    return { 'X-Custom-Domain': hostname };
  }
  return {};
};

/**
 * Update theme-color meta tag based on branding.
 */
const updateThemeColor = (themeColor) => {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.content = themeColor;
};

/**
 * Dynamically update the browser tab favicon.
 */
const updateFavicon = (url) => {
  if (!url) return;
  let link = document.querySelector('link[rel="icon"]');
  if (link) {
    link.href = url;
  } else {
    link = document.createElement('link');
    link.rel = 'icon';
    link.href = url;
    document.head.appendChild(link);
  }
  // Also update apple-touch-icon for mobile "Add to Home Screen"
  let appleLink = document.querySelector('link[rel="apple-touch-icon"]');
  if (appleLink) {
    appleLink.href = url;
  }
};

/**
 * Apply branding to DOM — title, theme color, and favicon.
 *
 * In an institute context an unknown name is an EMPTY title and an unknown
 * icon is a blank one — never ours (see EMPTY_BRAND). public/index.html has
 * already done the same before the bundle loaded.
 */
const applyBrandingToDOM = (b) => {
  const institute = isInstituteContext();
  document.title = b.brandName || (institute ? '' : DEFAULTS.brandName);
  // The token overrides — this is what makes Settings → Branding actually change
  // the app rather than just the database (audit F7). Everything downstream reads
  // hsl(var(--primary)), so the whole chrome follows from these five properties.
  applyBrandTokens(b.appTheme || null);
  updateThemeColor(b.primaryColor || DEFAULTS.primaryColor);
  if (b.faviconUrl) updateFavicon(b.faviconUrl);
  else if (b.logoUrl) updateFavicon(b.logoUrl);
  else if (institute) updateFavicon(BLANK_FAVICON);
  // Per-institute installed-PWA icon / splash / iOS startup image. No-ops unless
  // the institute has a real http(s) icon, so the default install is untouched.
  applyDynamicPwaBranding(b);
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within BrandingProvider');
  }
  return context;
};

/** The branding when a provider is mounted, else null — for hooks used outside one. */
export const useBrandingOptional = () => useContext(BrandingContext) || null;

export const BrandingProvider = ({ children }) => {
  // Initialize from cache immediately (stale-while-revalidate pattern)
  const cached = getCachedBranding();
  // Before anything is known: the cache, else nothing (an institute's page) or
  // the build-time DEFAULTS (our own page, or an enterprise build's operator).
  const initial = cached?.data || (isInstituteContext() ? EMPTY_BRAND : DEFAULTS);
  const [branding, setBranding] = useState(initial);
  const [loading, setLoading] = useState(!cached?.data);

  // Apply branding to the DOM immediately (before any API call). Use cached data
  // if present, else the build-time DEFAULTS — so an enterprise build paints the
  // operator's title/favicon on first load instead of the bundled default.
  useEffect(() => {
    applyBrandingToDOM(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mergeBranding = useCallback((platform, institute) => {
    const hasCustomBranding = !!(institute.brandName || institute.logoUrl || institute.faviconUrl);

    // An institute's page never borrows the platform's identity: no name, logo,
    // favicon or subtitle of ours, even when the institute has not set its own.
    // The platform row still supplies the non-identity fields (support email,
    // a platform-wide app theme an institute has not overridden).
    if (isInstituteContext()) {
      return {
        brandName: institute.brandName || '',
        legalName: institute.legalName || DEFAULTS.legalName,
        brandSubtitle: institute.brandSubtitle || '',
        primaryColor: institute.primaryColor || DEFAULTS.primaryColor,
        appTheme: normalizeAppTheme(institute.appTheme) || normalizeAppTheme(platform.appTheme) || DEFAULTS.appTheme,
        logoBgColor: institute.logoBgColor || DEFAULTS.logoBgColor,
        logoUrl: institute.logoUrl || null,
        faviconUrl: institute.faviconUrl || null,
        pwaIconUrl: institute.pwaIconUrl || null,
        pwaSplashUrl: institute.pwaSplashUrl || null,
        supportEmail: platform.supportEmail || DEFAULTS.supportEmail,
        contactEmail: institute.contactEmail || DEFAULTS.contactEmail,
        loginPage: institute.loginPage || null,
      };
    }

    const domainBrandName = isLmsCampus() ? DEFAULTS.brandName : null;

    return {
      brandName: (hasCustomBranding && institute.brandName) || domainBrandName || platform.brandName || DEFAULTS.brandName,
      // Company legal name (e.g. brand "Google" → legal "Alphabet Inc."). No
      // platform-level legal name; falls back to '' so consumers use brandName.
      legalName: institute.legalName || DEFAULTS.legalName,
      brandSubtitle: (hasCustomBranding && institute.brandSubtitle) || platform.brandSubtitle || DEFAULTS.brandSubtitle,
      primaryColor: (hasCustomBranding && institute.primaryColor) || platform.primaryColor || DEFAULTS.primaryColor,
      // ⚠️ NOT gated on `hasCustomBranding`. That flag asks "has this institute
      // set a name or a logo", which is the wrong question for a colour — it is
      // exactly how uploading a logo used to switch on a brand colour nobody had
      // chosen. `appTheme.mode` is the institute's own answer, so it is the only
      // gate. An institute row wins over the platform row; neither means default.
      appTheme: normalizeAppTheme(institute.appTheme) || normalizeAppTheme(platform.appTheme) || DEFAULTS.appTheme,
      logoBgColor: (hasCustomBranding && institute.logoBgColor) || platform.logoBgColor || DEFAULTS.logoBgColor,
      logoUrl: (hasCustomBranding && institute.logoUrl) || platform.logoUrl || DEFAULTS.logoUrl,
      faviconUrl: (hasCustomBranding && institute.faviconUrl) || platform.faviconUrl || DEFAULTS.faviconUrl,
      // PWA install assets — fall back to favicon/logo inside applyDynamicPwaBranding.
      pwaIconUrl: (hasCustomBranding && institute.pwaIconUrl) || platform.pwaIconUrl || DEFAULTS.pwaIconUrl,
      pwaSplashUrl: (hasCustomBranding && institute.pwaSplashUrl) || platform.pwaSplashUrl || DEFAULTS.pwaSplashUrl,
      supportEmail: platform.supportEmail || DEFAULTS.supportEmail,
      // The INSTITUTE's public address, never the platform's — `supportEmail`
      // above is ours, and a white-labelled page must not fall back to it.
      contactEmail: institute.contactEmail || DEFAULTS.contactEmail,
      // The institute's own login-page panel (Settings → Branding), null when off.
      loginPage: institute.loginPage || null,
    };
  }, []);

  const fetchBranding = useCallback(async () => {
    try {
      const headers = getBrandingHeaders();
      const user = getCurrentUser();
      const rawInstId = user?.instituteId;
      const instId = rawInstId && typeof rawInstId === 'object' ? (rawInstId._id || rawInstId) : rawInstId;
      const subdomain = getSubdomain();
      const isCustomDomain = !isLmsCampus() && window.location.hostname !== 'localhost';
      const shouldFetchInstitute = !!(instId || subdomain || isCustomDomain);

      // Fire platform + institute branding requests in parallel (single round-trip latency)
      const platformReq = axios.get(`${getBaseUrl()}/platform/branding`, { headers }).catch(() => null);
      const instituteReq = shouldFetchInstitute
        ? axios.get(
            instId
              ? `${getBaseUrl()}/institute/branding?instituteId=${instId}`
              : `${getBaseUrl()}/institute/branding`,
            { headers }
          ).catch(() => null)
        : Promise.resolve(null);

      const [platformRes, instituteRes] = await Promise.all([platformReq, instituteReq]);

      const platform = platformRes?.data?.branding || {};
      const institute = instituteRes?.data?.branding || {};

      // Merge and apply
      const merged = mergeBranding(platform, institute);
      setBranding(merged);
      applyBrandingToDOM(merged);

      // Cache the result
      setCachedBranding(merged);
    } catch (err) {
      console.warn('Failed to fetch branding, using defaults:', err.message);
    } finally {
      setLoading(false);
    }
  }, [mergeBranding]);

  useEffect(() => {
    // If cached data exists and isn't expired, skip immediate fetch (background refresh)
    if (cached?.data && !cached.expired) {
      setLoading(false);
      // Still refresh in background after a short delay to catch any updates
      const timer = setTimeout(fetchBranding, 3000);
      return () => clearTimeout(timer);
    }
    // No cache or expired — fetch immediately
    fetchBranding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchBranding]);

  // `isInstituteContext` is re-read whenever branding changes, which includes
  // the refresh Login triggers — so it follows a sign-in on our own host.
  const contextValue = useMemo(() => ({
    ...branding, loading, refreshBranding: fetchBranding, isInstituteContext: isInstituteContext(),
  }), [branding, loading, fetchBranding]);

  return (
    <BrandingContext.Provider value={contextValue}>
      {children}
    </BrandingContext.Provider>
  );
};
